// Phone (< md) versions of the positions and orders tables: one stacked card per row,
// label: value pairs, the main number large on the right. Same pattern as Trade History's phone cards.
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { fmtDateTime, fmtPnl, pnlCls } from "./tradeFormat";

const dirCls = (good) => good
  ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]'
  : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]';

function Field({ label, value, cls = "text-[hsl(var(--ops-text))]" }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">{label}</div>
      <div className={`text-xs font-mono-num truncate ${cls}`}>{value ?? '—'}</div>
    </div>
  );
}

const price = (v) => (v != null ? v.toFixed(2) : '—');

// full = the Live Trading page (adds setup, score and entry time); otherwise the Overview preview.
export function PositionCards({ positions, full = false, testId }) {
  return (
    <div data-testid={testId} className="md:hidden divide-y divide-[hsl(var(--ops-border))]">
      {positions.map((p, i) => (
        <div key={i} data-testid="ops-position-card" className="px-3 py-2.5">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap">
              <Badge className={`rounded text-[10px] border-0 px-1.5 gap-0.5 ${dirCls(p.direction === 'LONG')}`}>
                {p.direction === 'LONG' ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
                {p.direction}
              </Badge>
              <span className="text-sm font-medium text-[hsl(var(--ops-text))]">{p.instrument}</span>
              {p.quantity != null && <span className="text-[11px] font-mono-num text-[hsl(var(--ops-text-muted))]">× {p.quantity}</span>}
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">Unrealized</div>
              <div className={`text-base font-mono-num font-semibold ${pnlCls(p.unrealized_pnl)}`}>{fmtPnl(p.unrealized_pnl)}</div>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1.5">
            <Field label="Entry" value={price(p.entry_price)} />
            <Field label="Current" value={price(p.current_price)} />
            <Field label="Stop" value={price(p.stop_loss)} cls="text-[hsl(var(--ops-warn))]" />
            <Field label="Target" value={price(p.profit_target)} cls="text-[hsl(var(--ops-brand))]" />
            {full && <Field label="Score" value={p.mentor_score} cls="text-[hsl(var(--ops-brand))]" />}
            {full && <Field label="Opened" value={p.entry_time ? fmtDateTime(p.entry_time, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'} />}
          </div>
          {full && p.setup && (
            <div className="mt-1.5 text-[11px] text-[hsl(var(--ops-text-muted))] break-words">Setup: <span className="text-[hsl(var(--ops-text))]">{p.setup}</span></div>
          )}
        </div>
      ))}
    </div>
  );
}

export function OrderCards({ orders, testId }) {
  return (
    <div data-testid={testId} className="md:hidden divide-y divide-[hsl(var(--ops-border))]">
      {orders.map((o, i) => (
        <div key={i} data-testid="ops-order-card" className="px-3 py-2.5">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge className={`rounded text-[10px] border-0 px-1.5 ${dirCls(o.direction === 'BUY')}`}>{o.direction}</Badge>
                <span className="text-sm font-medium text-[hsl(var(--ops-text))]">{o.instrument}</span>
                {o.quantity != null && <span className="text-[11px] font-mono-num text-[hsl(var(--ops-text-muted))]">× {o.quantity}</span>}
                <Badge className="rounded text-[10px] bg-[hsl(var(--ops-warn))]/15 text-[hsl(var(--ops-warn))] border-0">{o.status?.toUpperCase()}</Badge>
              </div>
              <div className="text-[11px] text-[hsl(var(--ops-text-muted))] break-words">
                {o.type || '—'}{o.label ? ` · ${o.label}` : ''}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">Price</div>
              <div className="text-base font-mono-num font-semibold text-[hsl(var(--ops-text))]">{price(o.price)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
